/**
 * Admin Audit Log Tab
 * View and filter admin action audit logs
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, Filter, ChevronLeft, ChevronRight, 
  Clock, User, FileText, Trash2, CheckCircle, XCircle, RefreshCw
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../lib/api";

const AdminAuditLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, skip: 0 });
  const [filters, setFilters] = useState({
    admin_email: "",
    action: "",
    resource_type: ""
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        skip: pagination.skip.toString()
      });
      if (filters.admin_email) params.append("admin_email", filters.admin_email);
      if (filters.action) params.append("action", filters.action);
      if (filters.resource_type) params.append("resource_type", filters.resource_type);

      const res = await axios.get(`${API}/audit-logs?${params}`, {
        withCredentials: true
      });
      setLogs(res.data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: res.data.total || 0
      }));
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.skip, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const clearFilters = () => {
    setFilters({ admin_email: "", action: "", resource_type: "" });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "approve": return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "reject": return <XCircle className="w-4 h-4 text-red-600" />;
      case "delete": return <Trash2 className="w-4 h-4 text-red-600" />;
      case "create": return <FileText className="w-4 h-4 text-blue-600" />;
      default: return <FileText className="w-4 h-4 text-stone-600" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "approve": return "bg-green-100 text-green-700";
      case "reject": return "bg-red-100 text-red-700";
      case "delete": return "bg-red-100 text-red-700";
      case "create": return "bg-blue-100 text-blue-700";
      default: return "bg-stone-100 text-stone-700";
    }
  };

  const getResourceTypeLabel = (type) => {
    const labels = {
      memorial: "Memorial",
      blog_submission: "Blog Submission",
      blog_post: "Blog Post",
      gallery_image: "Gallery Image",
      social_post: "Social Post"
    };
    return labels[type] || type;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="space-y-6" data-testid="admin-audit-log-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-600" />
                Admin Audit Log
              </CardTitle>
              <CardDescription>Track all administrative actions for security and accountability</CardDescription>
            </div>
            <Button variant="outline" onClick={fetchLogs} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 p-4 bg-stone-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-stone-500" />
              <span className="text-sm font-medium text-stone-600">Filters:</span>
            </div>
            
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Filter by admin email..."
                value={filters.admin_email}
                onChange={(e) => handleFilterChange("admin_email", e.target.value)}
                className="h-9"
                data-testid="audit-filter-email"
              />
            </div>

            <Select value={filters.action || "all"} onValueChange={(v) => handleFilterChange("action", v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px] h-9" data-testid="audit-filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="create">Create</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.resource_type || "all"} onValueChange={(v) => handleFilterChange("resource_type", v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px] h-9" data-testid="audit-filter-resource">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                <SelectItem value="memorial">Memorials</SelectItem>
                <SelectItem value="blog_submission">Blog Submissions</SelectItem>
                <SelectItem value="blog_post">Blog Posts</SelectItem>
                <SelectItem value="gallery_image">Gallery Images</SelectItem>
                <SelectItem value="social_post">Social Posts</SelectItem>
              </SelectContent>
            </Select>

            {(filters.admin_email || filters.action || filters.resource_type) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-stone-600">
              Showing {logs.length} of {pagination.total} entries
            </p>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-700 mb-2">No Audit Logs Found</h3>
              <p className="text-stone-500">
                {filters.admin_email || filters.action || filters.resource_type
                  ? "Try adjusting your filters"
                  : "Admin actions will appear here when performed"}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead>Admin</TableHead>
                    <TableHead className="w-[100px]">Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="w-[120px]">IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log, index) => (
                    <TableRow key={index} className="hover:bg-stone-50" data-testid={`audit-row-${index}`}>
                      <TableCell className="font-mono text-xs text-stone-600">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-stone-400" />
                          <span className="text-sm">{log.admin_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getActionColor(log.action)} flex items-center gap-1 w-fit`}>
                          {getActionIcon(log.action)}
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{getResourceTypeLabel(log.resource_type)}</span>
                          {log.resource_id && (
                            <span className="text-xs text-stone-400 ml-2 font-mono">
                              {log.resource_id}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <div className="text-xs text-stone-600 truncate">
                            {Object.entries(log.details).map(([key, value]) => (
                              value && <span key={key} className="mr-2">{key}: {String(value).substring(0, 30)}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-stone-500">
                        {log.ip_address || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-stone-600">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLogTab;
